import React, { Suspense, lazy, useState } from 'react';
import { useSecretaryStore } from '@/modules/secretary/store/secretaryStore';
import SecretaryLayout from '@/presentation/layouts/SecretaryLayout';
import { PauseOctagon, Play, CreditCard, CalendarDays, Plus, Info, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LimboBanner } from '@/modules/secretary/components/LimboBanner';
import { NuevaCitaSlideOver } from '@/modules/secretary/components/NuevaCitaSlideOver';
import { VistaCitaSlideOver } from '@/modules/secretary/components/VistaCitaSlideOver';
import { CheckoutCitaSlideOver } from '@/modules/secretary/components/CheckoutCitaSlideOver';
import { BuscadorInteligenteModal } from '@/modules/secretary/components/BuscadorInteligenteModal';
import { NuevaCitaEquipoSlideOver } from '@/modules/secretary/components/NuevaCitaEquipoSlideOver';

// ── Carga diferida (Lazy) para cada componente hermético ──────────────────────
// Si un componente falla, el otro sigue funcionando.
const PaymentQueue = lazy(() => import('@/modules/secretary/components/PaymentQueue'));
const CalendarGrid = lazy(() => import('@/modules/secretary/components/CalendarGrid'));
const TeamCalendarGrid = lazy(() =>
  import('@/modules/secretary/components/TeamCalendarGrid').then((m) => ({ default: m.TeamCalendarGrid }))
);

type Tab = 'calendario' | 'pagos';

// ─── Overlay de Pausa Activa ──────────────────────────────────────────────────

function PausaActivaOverlay() {
  const { pausaActiva, reanudarPausa } = useSecretaryStore();
  if (!pausaActiva) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-amber-500/30 p-4 rounded-2xl shadow-2xl shadow-amber-500/10 animate-in slide-in-from-bottom-8 duration-300">
      <div className="flex items-center gap-3">
        <div className="relative p-2 rounded-full bg-amber-500/20 text-amber-500">
          <div className="absolute inset-0 rounded-full bg-amber-500/40 blur-md animate-pulse" />
          <PauseOctagon className="h-6 w-6 relative z-10" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
            Pausa Activa
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Validando pago de <strong className="text-amber-600 dark:text-amber-400">{pausaActiva.citaNombre}</strong>
          </span>
        </div>
      </div>
      
      <div className="w-px h-8 bg-slate-200 dark:bg-slate-700 mx-2" />
      
      <button
        onClick={reanudarPausa}
        className="h-10 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-sm flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95 shadow-sm"
      >
        <Play className="h-4 w-4 fill-slate-900" />
        Retomar
      </button>
    </div>
  );
}

// ─── Toasts de Notificaciones ────────────────────────────────────────────────
function ToastsContainer() {
  const { notificaciones, removeNotificacion } = useSecretaryStore();
  
  if (notificaciones.length === 0) return null;

  return (
    <div className="fixed top-6 right-6 z-9999 flex flex-col gap-2 pointer-events-none">
      {notificaciones.map((n) => (
        <div 
          key={n.id} 
          className={cn(
            "pointer-events-auto flex items-start gap-3 p-4 pr-10 rounded-xl shadow-lg border animate-in slide-in-from-right-8 fade-in duration-300 relative overflow-hidden",
            n.type === 'success' && "bg-emerald-50 text-emerald-800 border-emerald-200 shadow-emerald-900/5",
            n.type === 'warning' && "bg-amber-50 text-amber-800 border-amber-200 shadow-amber-900/5",
            n.type === 'info' && "bg-blue-50 text-blue-800 border-blue-200 shadow-blue-900/5",
          )}
        >
          {n.type === 'success' && <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />}
          {n.type === 'warning' && <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />}
          {n.type === 'info' && <Info className="h-5 w-5 text-blue-600 mt-0.5" />}
          
          <p className="text-sm font-medium leading-tight">
            {n.message}
          </p>
          
          <button 
            onClick={() => removeNotificacion(n.id)}
            className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/5 transition-colors"
          >
            <X className="h-4 w-4 opacity-50" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Navegación por Pestañas ──────────────────────────────────────────────────

function TabNav({
  active,
  onChange,
  pendingCount,
}: {
  readonly active: Tab;
  readonly onChange: (tab: Tab) => void;
  readonly pendingCount: number;
}) {
  const tabs: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
    {
      id: 'calendario',
      label: 'Agenda del día',
      icon: <CalendarDays className="h-4 w-4" />,
    },
    {
      id: 'pagos',
      label: 'Cola de Pagos',
      icon: <CreditCard className="h-4 w-4" />,
    },
  ];

  return (
    <div className="flex items-center gap-2 md:gap-6 pb-0">
      {tabs.map(({ id, label, icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            'flex items-center justify-center gap-2 px-2 h-14 text-sm font-medium border-b-2 transition-colors -mb-px bg-transparent cursor-pointer',
            active === id
              ? 'border-[#0052CC] text-[#0052CC] border-solid'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          )}
        >
          {icon}
          {label}
          {id === 'pagos' && pendingCount > 0 && (
            <span className="ml-1 min-w-4.5 h-4.5 px-1 flex items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold">
              {pendingCount}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Dashboard Principal de Secretaría ───────────────────────────────────────

export default function SecretaryDashboard() {
  const {
    citaSeleccionada,
    citaCalendarioSeleccionada,
    citasPendientes,
    pausaActiva,
    activarPausa,
    setNuevaCitaOpen,
    setBuscadorInteligenteOpen,
    lastActionText,
    activeWorkspaceType,
  } = useSecretaryStore();

  const [activeTab, setActiveTab] = useState<Tab>('calendario');

  // La tarea activa puede ser del calendario o de la cola de pagos
  const tareaActiva = citaSeleccionada ?? citaCalendarioSeleccionada;
  const nombreTareaActiva = tareaActiva
    ? `cita de ${tareaActiva.paciente_nombre}`
    : null;

  return (
    <SecretaryLayout>
      <div 
        className={cn(
          "relative flex flex-col h-full gap-3 transition-all duration-500",
          pausaActiva && "ring-4 ring-amber-500/30 bg-amber-50/10 rounded-2xl p-2 -m-2"
        )}
      >
        {/* Cabecera consolidada: Pestañas + Acciones contextuales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 shrink-0 border-b border-slate-200 dark:border-slate-800 pb-0 w-full">
          {/* Izquierda: Tabs */}
          <div className="flex items-center gap-0 overflow-x-auto custom-scrollbar">
            <TabNav active={activeTab} onChange={setActiveTab} pendingCount={citasPendientes.length} />
          </div>

          {/* Derecha: Acciones contextuales según vista activa */}
          <div className="flex flex-wrap items-center gap-2 shrink-0 pb-2">
            {nombreTareaActiva && !pausaActiva && (
              <button
                onClick={() => activarPausa(
                  tareaActiva!.cita_id,
                  nombreTareaActiva,
                  !!citaCalendarioSeleccionada
                )}
                className="h-11 px-4 text-sm font-bold rounded-xl border-2 border-amber-500/30 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-500/50 flex items-center gap-2 cursor-pointer transition-all shadow-sm"
              >
                <PauseOctagon className="h-4 w-4" />
                Pausar Tarea
              </button>
            )}

            {/* Buscador Inteligente: disponible en ambas vistas (individual y equipos) */}
            {activeTab === 'calendario' && (
              <button
                onClick={() => setBuscadorInteligenteOpen(true)}
                title="Buscador Inteligente"
                className="h-11 w-11 flex justify-center items-center rounded-xl border-2 border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 cursor-pointer transition-all shadow-sm"
              >
                <CalendarDays className="h-5 w-5" />
              </button>
            )}

            {/* Nueva Cita: contexto individual */}
            {activeTab === 'calendario' && activeWorkspaceType === 'personal' && (
              <button
                onClick={() => setNuevaCitaOpen(true)}
                title="Nueva Cita"
                className="h-11 w-11 flex justify-center items-center rounded-xl border-2 border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:border-blue-700 cursor-pointer transition-all shadow-sm shadow-blue-500/20"
              >
                <Plus className="h-5 w-5" />
              </button>
            )}

            {/* Nueva Cita Grupal: contexto equipos */}
            {activeTab === 'calendario' && activeWorkspaceType === 'equipo' && (
              <button
                type="button"
                onClick={() => useSecretaryStore.getState().setNuevaCitaEquipoOpen(true)}
                title="Nueva Cita Grupal"
                className="h-11 px-4 flex items-center gap-2 rounded-xl border-2 border-indigo-600 bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 hover:border-indigo-700 cursor-pointer transition-all shadow-sm shadow-indigo-500/20"
              >
                <Plus className="h-4 w-4" />
                Cita Grupal
              </button>
            )}
          </div>
        </div>
        
        {/* Historial de Continuidad (Migas de Pan) */}
        {lastActionText && (
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md w-fit -mt-1 animate-in fade-in duration-500">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500/50" />
            Última acción: {lastActionText}
          </div>
        )}



        {/* Contenido de la Pestaña Activa */}
        <div className="flex-1 min-h-0 relative">
          {/* La tarjeta de pausa ahora es global al layout, la renderizamos junto a los bottom elements */}

          <Suspense
            fallback={
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Cargando...
              </div>
            }
          >
            {activeTab !== 'calendario' && <PaymentQueue />}
            {activeTab === 'calendario' && activeWorkspaceType === 'equipo' && <TeamCalendarGrid />}
            {activeTab === 'calendario' && activeWorkspaceType === 'personal' && <CalendarGrid />}
          </Suspense>

          <LimboBanner />
          <NuevaCitaSlideOver />
          <VistaCitaSlideOver />
          <CheckoutCitaSlideOver />
          <BuscadorInteligenteModal />
          <NuevaCitaEquipoSlideOver />
          <PausaActivaOverlay />
          <ToastsContainer />
        </div>
      </div>
    </SecretaryLayout>
  );
}
